import { Box, Dialog, DialogContent, Grid, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    EXCLUDED_COLS,
    generateDataGridObjectUpdates,
    generateDefaultRecord,
    getRowsFromSif,
    mapLineItemsDataToMutationJSON
} from './utils';
import { CustomDialogActions, CustomDialogTitle, PreviewGridSection, FileSection, RenderActions } from './components';
import { GridApiPro, GridColDef, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { IRecordHeaders } from '../types';
import { useLineItemsData, useLineItemsMutations } from '../LineItems/hooks';
import { filterNotAllowedFields } from '../utils';
import { ILineItemsFields } from '../LineItems/types';
import { useRecordData } from 'ui-component/RecordView/PropertiesPanel/hooks';
import { getDataToCreate } from 'ui-component/RecordView/utils';
import { useMutation } from '@apollo/client';
import { IRegisterRecord, RegisterRecordVariablesShape } from 'views/CreateRecord/types';
import { MUTATION_REGISTER_RECORD } from 'graphql/mutations/records';
import { DetailPanel } from './components/DetailPanel';
import { splitHeaders } from '../utils/headerHelpers';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { useCustomListValues } from 'hooks/useCustomListValues';

export type ImportRecordProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    sifFile: File | null;
    apiRef?: React.MutableRefObject<GridApiPro>;
    recordTypeId: number;
    showObjectPanel: boolean;
    ObjectPanelContainer: () => JSX.Element;
    objectsToCreate: Record<string, any[]>;
};

export const ImportRecord = ({
    open,
    onClose,
    onSubmit,
    sifFile,
    apiRef,
    recordTypeId,
    showObjectPanel,
    ObjectPanelContainer,
    objectsToCreate
}: ImportRecordProps) => {
    const dispatch = useDispatch();

    const intl = useIntl();
    const { palette } = useTheme();
    const previewGridApiRef = useGridApiRef();

    const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
    const [selectedSifFile, setSelectedSifFile] = useState<File | null>(sifFile || null);
    const [selectedMergeMode, setSelectedMergeMode] = useState('IMPORT_ALL');
    const [rows, setRows] = useState<Record<string, any>[]>([]);
    const [lineItemsRows, setLineItemRows] = useState<Record<string, any>[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const lineItemsGridRef = useRef<any>(null);
    const { lineItemHeaders } = useLineItemsData(recordTypeId as number);

    const { fieldAccess, headers, loading: loadingRecordData } = useRecordData({ recordTypeId: Number(recordTypeId) });
    const recordGridCols = useMemo(() => apiRef?.current?.getAllColumns().filter((el) => !EXCLUDED_COLS.includes(el.field)), [apiRef]);

    const requiredColsHeaders = useMemo(
        () =>
            headers
                ? recordGridCols?.filter((col) => headers[col.field]?.required).map((col) => ({ ...headers[col.field], field: col.field }))
                : undefined,
        [headers, recordGridCols]
    );

    const { loading: loadingListValues, listValuesByListId } = useCustomListValues(
        requiredColsHeaders
            ?.filter(
                (col) => ['dropdown', 'multiselect', 'multiselect-checkbox', 'single-select'].includes(col.dataType) && col.listType?.id
            )
            .map((col) => Number(col?.listType?.id)) || []
    );

    const checkAllowedFields = useMemo(() => filterNotAllowedFields(fieldAccess), [fieldAccess]);
    const { baseFieldHeaders, additionalFieldHeaders } = useMemo(
        () => splitHeaders(headers || null, checkAllowedFields),
        [checkAllowedFields, headers]
    );

    const [registerRecord] = useMutation<IRegisterRecord, RegisterRecordVariablesShape>(MUTATION_REGISTER_RECORD);
    const { createOrUpdateByRow } = useLineItemsMutations();

    const handleToggleEdit = (rowId: string | number) => () => {
        if (previewGridApiRef.current.state.editRows[rowId]) previewGridApiRef.current.stopRowEditMode({ id: rowId });
        else previewGridApiRef.current.startRowEditMode({ id: rowId });
    };

    const handleClickDelete = (rowId: string | number) => () => setRows((prevRows) => prevRows.filter((row) => row.id !== rowId));

    const columns = apiRef
        ? [
              ...(recordGridCols || []),
              {
                  field: 'actions',
                  headerName: intl.formatMessage({ id: 'actions', defaultMessage: 'Actions' }),
                  renderHeader: (params) => (
                      <Typography fontSize="16px" fontWeight={700} color="#000000">
                          {params.colDef.headerName}
                      </Typography>
                  ),
                  renderCell: (params) => (
                      <RenderActions
                          key={previewGridApiRef.current.state.editRows[params.id] ? `${params.id}-editing` : `${params.id}-view`}
                          onToggleEdit={handleToggleEdit(params.id)}
                          onDelete={handleClickDelete(params.id)}
                          rowId={params.id}
                          apiRef={previewGridApiRef}
                      />
                  )
              } as GridColDef
          ]
        : [];

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            // create record
            const { baseData, additionalFields } = getDataToCreate(
                rows[0],
                baseFieldHeaders,
                additionalFieldHeaders,
                fieldAccess,
                objectsToCreate
            );
            const tenant = Number(localStorage.getItem('tenant_id'));
            const { data: registerData } = await registerRecord({
                variables: {
                    createRecordHeader: {
                        enabled: true,
                        recordFileId: undefined,
                        createdBy: baseData?.createdBy,
                        additionalFields,
                        tenant,
                        recordType: baseData?.recordType
                    }
                }
            });

            const newRecordId = registerData?.registerRecordHeader.id;

            // create line items
            const lineItemsToCreate = mapLineItemsDataToMutationJSON(lineItemsRows, Number(newRecordId));
            const lineItemsPromises = lineItemsToCreate
                .filter((item) => item?.lineItemsFields?.length)
                .map((item) =>
                    createOrUpdateByRow({
                        variables: { data: item }
                    })
                );
            const results = await Promise.allSettled(lineItemsPromises);
            let fulfilledCount = 0;
            let rejectedCount = 0;

            results.forEach((result) => {
                if (result.status === 'fulfilled') fulfilledCount += 1;
                if (result.status === 'rejected') rejectedCount += 1;
            });
            dispatch(
                openSnackbar({
                    open: true,
                    type: rejectedCount ? 'error' : 'success',
                    title: 'Import completed',
                    subtitle: `${fulfilledCount} item created successfully${
                        rejectedCount ? `, ${rejectedCount} errors occurred. Items with errors were left selected for review.` : ''
                    }`
                })
            );
            setTimeout(onSubmit, 2000);
        } catch (error) {
            console.log(error);
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Error',
                    subtitle: 'Failed to create the SIF record and line items'
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const adjustLineItemRowsByLineItemHeaders = useCallback(
        (rowList: Record<string, any>[]) => {
            const newList = [];

            const lineItemFieldsByName = (lineItemHeaders?.lineItemsByTypeFileds || []).reduce<Record<string, ILineItemsFields>>(
                (acc, field) => {
                    acc[field.recordAdditionalFields.name] = field;
                    return acc;
                },
                {}
            );

            for (const row of rowList) {
                const newRow = Object.fromEntries(
                    Object.entries(row).map(([key, value]) => {
                        const lineItemFieldDef = lineItemFieldsByName[key];
                        if (lineItemFieldDef) return [`${key}-${lineItemFieldDef.order}-${lineItemFieldDef.id}`, value];
                        return [key, value];
                    })
                );
                newList.push(newRow);
            }

            setLineItemRows(newList);
            setTimeout(() => {
                previewGridApiRef.current?.autosizeColumns({ includeHeaders: true });
            }, 1000);
        },
        [lineItemHeaders?.lineItemsByTypeFileds, previewGridApiRef]
    );

    useEffect(() => {
        if (selectedSifFile && requiredColsHeaders && !loadingListValues) {
            setRows([generateDefaultRecord(recordTypeId, requiredColsHeaders, listValuesByListId)]);
            getRowsFromSif(selectedSifFile, adjustLineItemRowsByLineItemHeaders);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSifFile, recordTypeId, adjustLineItemRowsByLineItemHeaders, requiredColsHeaders, loadingListValues]);

    useEffect(() => {
        if (!Object.keys(objectsToCreate).length) return;
        const { newRowHeaders, newAdditionalFields } = generateDataGridObjectUpdates(objectsToCreate, headers);
        setRows((curRows) => [
            {
                ...(curRows[0] || {}),
                ...newRowHeaders,
                additionalFields: [...(curRows[0]?.additionalFields || []), ...newAdditionalFields]
            }
        ]);
    }, [objectsToCreate, headers]);

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { borderRadius: '8px', py: 0 } }}
        >
            <CustomDialogTitle onClose={onClose} title={intl.formatMessage({ id: 'importRecords', defaultMessage: 'Import Records' })} />

            <DialogContent sx={{ overflowY: 'hidden', height: '80vh', p: 0, bgcolor: palette.primary[300] }}>
                <Grid container sx={{ height: '100%', maxWidth: '100%' }} flexWrap="nowrap">
                    <FileSection
                        onChangeMergeMode={setSelectedMergeMode}
                        onChangeSelectedSifFile={(val) => {
                            setSelectedSifFile(val);
                            if (val === null) {
                                setRows([]);
                                setSelectedRows([]);
                            }
                        }}
                        selectedMergeMode={selectedMergeMode}
                        selectedSifFile={selectedSifFile}
                    />
                    <PreviewGridSection
                        rows={rows}
                        columns={columns}
                        selectedRows={selectedRows}
                        onChangeSelectedRows={setSelectedRows}
                        onUpdateRow={(newRow) => {
                            const updatedRows = rows.map((row) => (row.id === newRow.id ? newRow : row));
                            setRows(updatedRows);
                        }}
                        apiRef={previewGridApiRef}
                        getDetailPanelContent={
                            lineItemsRows
                                ? ({ row }: GridRowParams<IRecordHeaders>) => (
                                      <DetailPanel
                                          lineItemsRows={lineItemsRows}
                                          setLineItemRows={setLineItemRows}
                                          lineItemsGridRef={lineItemsGridRef}
                                          recordTypeId={recordTypeId}
                                      />
                                  )
                                : undefined
                        }
                        getDetailPanelHeight={() => 300}
                        isImportRecordAndObjectPanelOpened={showObjectPanel}
                    />

                    {showObjectPanel && (
                        <Box width="300px">
                            <ObjectPanelContainer />
                        </Box>
                    )}
                </Grid>
            </DialogContent>

            <CustomDialogActions
                onClose={onClose}
                handleSubmit={handleSubmit}
                selectedRows={selectedRows}
                loadingSubmit={isSubmitting || loadingRecordData || loadingListValues}
            />
        </Dialog>
    );
};
